/* eslint-disable no-unused-vars */
import React, { ReactNode, useEffect, useRef, useState } from 'react';
import {
  Avatar,
  Badge,
  Box,
  Button,
  Flex,
  FormControl,
  FormErrorMessage,
  FormLabel,
  IconButton,
  Input,
  InputGroup,
  InputLeftElement,
  InputRightElement,
  Spinner,
  Text,
} from '@chakra-ui/react';
import { MagnifyingGlass, Plus, X } from 'phosphor-react';
import { FIELD_ADDON_WIDTH, FIELD_HEIGHT } from '../FieldGroup';

export interface ResponsibleUser {
  id: number;
  name: string;
  email?: string;
  cell_phone?: string;
  [key: string]: any;
}

export interface NewResponsible {
  name: string;
  email: string;
  phone: string;
  function?: string;
}

interface ResponsiblePickerProps {
  label?: string;
  searchInputId?: string;
  searchPlaceholder?: string;
  isSearching?: boolean;
  isLoadingEligible?: boolean;
  isDisabled?: boolean;
  disabledHint?: ReactNode;
  searchResults: ResponsibleUser[];
  eligibleUsers: ResponsibleUser[];
  selectedUsers: ResponsibleUser[];
  newResponsible?: NewResponsible | null;
  primaryHint?: string;
  error?: string;
  onSearchChange: (term: string) => void;
  onSelectUser: (user: ResponsibleUser) => void;
  onRemoveUser: (userId: number) => void;
  onCreateNew?: () => void;
  onEditNew?: () => void;
  onRemoveNew?: () => void;
  renderResultBadge?: (user: ResponsibleUser) => ReactNode;
  showPrimaryBadge?: boolean;
}

const UserRow = ({
  user,
  right,
  badge,
}: {
  user: { name: string; email?: string; cell_phone?: string };
  right?: ReactNode;
  badge?: ReactNode;
}) => (
  <Flex
    align="center"
    gap="12px"
    p="8px"
    pr="12px"
    borderRadius="lg"
    border="1px solid"
    borderColor="line.input"
    bg="surface.base"
    boxShadow="control"
    minW={0}
  >
    <Avatar
      size="sm"
      w="32px"
      h="32px"
      name={user.name}
      bg="surface.selected"
      color="content.body"
      fontSize="12px"
      fontWeight={500}
    />
    <Box flex="1" minW={0}>
      <Flex align="center" gap="8px" minW={0}>
        <Text fontSize="13px" lineHeight="20px" fontWeight={500} color="content.strong" isTruncated>
          {user.name}
        </Text>
        {badge}
      </Flex>
      <Text fontSize="12px" lineHeight="16px" color="content.subtle" isTruncated>
        {[user.email, user.cell_phone].filter(Boolean).join(' · ')}
      </Text>
    </Box>
    {right}
  </Flex>
);

export const ResponsiblePicker = ({
  label = 'Responsáveis',
  searchInputId = 'responsible-user-search',
  searchPlaceholder = 'Buscar por nome ou email',
  isSearching,
  isLoadingEligible,
  isDisabled,
  disabledHint,
  searchResults,
  eligibleUsers,
  selectedUsers,
  newResponsible,
  primaryHint,
  error,
  onSearchChange,
  onSelectUser,
  onRemoveUser,
  onCreateNew,
  onEditNew,
  onRemoveNew,
  renderResultBadge,
  showPrimaryBadge = true,
}: ResponsiblePickerProps) => {
  const [term, setTerm] = useState('');
  const [open, setOpen] = useState(false);
  const wrapperRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    const handler = (event: MouseEvent) => {
      if (!wrapperRef.current?.contains(event.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const selectedIds = new Set(selectedUsers.map((u) => u.id));
  const source = term.trim().length > 0 ? searchResults : eligibleUsers;
  const options = source.filter((u) => !selectedIds.has(u.id));
  const hasSelection = selectedUsers.length > 0 || Boolean(newResponsible);
  const showList = open && !isDisabled;

  const pick = (user: ResponsibleUser) => {
    onSelectUser(user);
    setTerm('');
    onSearchChange('');
    setOpen(false);
  };

  return (
    <FormControl isInvalid={Boolean(error)}>
      <Flex align="center" justify="space-between" gap="12px" mb="8px">
        <FormLabel htmlFor={searchInputId} mb={0}>
          {label}
        </FormLabel>
        {onCreateNew && (
          <Button
            type="button"
            size="sm"
            variant="outline"
            leftIcon={<Plus size={14} />}
            onClick={onCreateNew}
            isDisabled={isDisabled}
            data-testid="responsible-create-new"
          >
            Novo responsável
          </Button>
        )}
      </Flex>

      <Box ref={wrapperRef} position="relative">
        <InputGroup>
          <InputLeftElement
            pointerEvents="none"
            h={FIELD_HEIGHT}
            w={FIELD_ADDON_WIDTH}
            pl="1px"
            color="content.subtle"
          >
            {isSearching || isLoadingEligible ? (
              <Spinner size="xs" speed="0.6s" />
            ) : (
              <MagnifyingGlass size={14} />
            )}
          </InputLeftElement>
          <Input
            id={searchInputId}
            placeholder={searchPlaceholder}
            value={term}
            autoComplete="off"
            isDisabled={isDisabled}
            pl={FIELD_ADDON_WIDTH}
            pr={term ? FIELD_ADDON_WIDTH : undefined}
            onFocus={() => setOpen(true)}
            onChange={(e) => {
              setTerm(e.target.value);
              onSearchChange(e.target.value);
              setOpen(true);
            }}
          />
          {term && (
            <InputRightElement h={FIELD_HEIGHT} w={FIELD_ADDON_WIDTH}>
              <IconButton
                aria-label="Limpar busca"
                icon={<X size={12} />}
                size="xs"
                w="22px"
                h="22px"
                minW="22px"
                variant="ghost"
                color="content.subtle"
                onClick={() => {
                  setTerm('');
                  onSearchChange('');
                }}
              />
            </InputRightElement>
          )}
        </InputGroup>

        {showList && (
          <Box
            position="absolute"
            top="calc(100% + 4px)"
            left={0}
            right={0}
            zIndex={20}
            bg="surface.base"
            border="1px solid"
            borderColor="line.hairline"
            borderRadius="lg"
            boxShadow="popover"
            p="4px"
            maxH="260px"
            overflowY="auto"
          >
            {options.length === 0 ? (
              <Text px="12px" py="8px" fontSize="13px" color="content.faint">
                {isSearching || isLoadingEligible ? 'Carregando...' : 'Nenhum resultado encontrado'}
              </Text>
            ) : (
              <Box as="ul" listStyleType="none" m={0} p={0}>
                {options.map((user) => (
                  <Box
                    as="li"
                    key={user.id}
                    display="flex"
                    alignItems="center"
                    gap="10px"
                    px="8px"
                    py="6px"
                    borderRadius="sm"
                    cursor="pointer"
                    _hover={{ bg: 'surface.selected' }}
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={() => pick(user)}
                  >
                    <Avatar
                      size="xs"
                      w="24px"
                      h="24px"
                      name={user.name}
                      bg="surface.selected"
                      color="content.body"
                      fontSize="10px"
                    />
                    <Box flex="1" minW={0}>
                      <Text fontSize="13px" lineHeight="18px" color="content.strong" isTruncated>
                        {user.name}
                      </Text>
                      <Text fontSize="12px" lineHeight="16px" color="content.subtle" isTruncated>
                        {user.email}
                      </Text>
                    </Box>
                    {renderResultBadge?.(user)}
                  </Box>
                ))}
              </Box>
            )}
          </Box>
        )}
      </Box>

      {disabledHint && isDisabled && (
        <Text mt="8px" fontSize="12px" lineHeight="16px" color="content.subtle">
          {disabledHint}
        </Text>
      )}

      {hasSelection && (
        <Flex direction="column" gap="8px" mt="12px">
          {newResponsible && (
            <UserRow
              user={{
                name: newResponsible.name,
                email: newResponsible.email,
                cell_phone: newResponsible.phone,
              }}
              badge={<Badge variant="info">Novo</Badge>}
              right={
                <Flex align="center" gap="4px">
                  {onEditNew && (
                    <Button type="button" size="xs" variant="outline" onClick={onEditNew}>
                      Editar
                    </Button>
                  )}
                  {onRemoveNew && (
                    <IconButton
                      type="button"
                      aria-label="Remover novo responsável"
                      icon={<X size={14} />}
                      size="xs"
                      variant="ghost"
                      color="content.subtle"
                      onClick={onRemoveNew}
                    />
                  )}
                </Flex>
              }
            />
          )}
          {selectedUsers.map((user, index) => (
            <UserRow
              key={user.id}
              user={user}
              badge={
                showPrimaryBadge && !newResponsible && index === 0 ? (
                  <Badge variant="neutral">Principal</Badge>
                ) : undefined
              }
              right={
                <IconButton
                  type="button"
                  aria-label={`Remover ${user.name}`}
                  icon={<X size={14} />}
                  size="xs"
                  variant="ghost"
                  color="content.subtle"
                  onClick={() => onRemoveUser(user.id)}
                />
              }
            />
          ))}
        </Flex>
      )}

      {!hasSelection && !error && primaryHint && (
        <Text mt="8px" fontSize="12px" lineHeight="16px" color="content.subtle">
          {primaryHint}
        </Text>
      )}
      {error && <FormErrorMessage>{error}</FormErrorMessage>}
    </FormControl>
  );
};
